/**
 * The KnowIt adapter for the wisdom graph (W9 §A.1, §C.3).
 *
 * `WisdomBrowseController` (`wisdom.search/.node/.neighbors/.path/.provenance/.overview`)
 * answers BARE JSON — no `{data, policies}` envelope, unlike almost everything else this
 * estate talks to. `GET /api/wisdom/experts` is the one REST survivor (§E-3): the governed
 * family does not answer expert ranking, and it swallows every exception into an empty 200 —
 * "no experts" and "the query threw" are indistinguishable, which is why the UI never renders
 * an empty result as a fact (§E-3's decision).
 *
 * Every response here is parsed through the zod schemas in `types.ts` — a drifted field name
 * fails loudly at this boundary, which is the whole point after §A.1's finding that kms's own
 * client had drifted from the controllers it calls (`ConceptMatch` with no `id`/`score`,
 * `GraphEdge.source/target` vs the client's `sourceId/targetId`, and so on).
 */
import type {
  Expert,
  WisdomCoverage,
  WisdomFilters,
  WisdomNeighborhood,
  WisdomNode,
  WisdomOverview,
  WisdomPath,
  WisdomProvenance,
  WisdomSearchResult,
  WisdomTopDown,
} from "~/api/types";
import {
  ExpertSchema,
  WisdomCoverageSchema,
  WisdomNeighborhoodSchema,
  WisdomNodeSchema,
  WisdomOverviewSchema,
  WisdomPathSchema,
  WisdomProvenanceSchema,
  WisdomSearchResultSchema,
  WisdomTopDownSchema,
} from "~/api/types";

type Json = Record<string, unknown>;

function obj(value: unknown): Json {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Json) : {};
}
function arr(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

export interface WisdomTransport {
  /** `wisdom.*` — bare bodies, unwrap nothing. */
  post(path: string, body?: Record<string, unknown>): Promise<unknown>;
  /** `GET /api/wisdom/experts?q=` — the one REST survivor. */
  get(path: string): Promise<unknown>;
}

export function createKnowItWisdom(transport: WisdomTransport) {
  return {
    search: async (input: {
      query: string;
      kinds?: string[];
      k?: number;
      filters?: WisdomFilters;
    }): Promise<WisdomSearchResult> => {
      const raw = obj(
        await transport.post("wisdom.search", {
          query: input.query,
          ...(input.kinds ? { kinds: input.kinds } : {}),
          ...(input.k ? { k: input.k } : {}),
          ...(input.filters ? { filters: input.filters } : {}),
        }),
      );
      return WisdomSearchResultSchema.parse(raw);
    },

    node: async (input: {
      id: string;
      kind: string;
      rel?: string;
      offset?: number;
    }): Promise<WisdomNode | null> => {
      const raw = obj(
        await transport.post("wisdom.node", {
          id: input.id,
          kind: input.kind,
          ...(input.rel ? { rel: input.rel } : {}),
          ...(input.offset ? { offset: input.offset } : {}),
        }),
      );
      if (raw.found !== true) {
        return null;
      }
      return WisdomNodeSchema.parse(raw);
    },

    neighbors: async (input: {
      id: string;
      kind: string;
      depth?: 1 | 2;
      rels?: string[];
    }): Promise<WisdomNeighborhood | null> => {
      const raw = obj(
        await transport.post("wisdom.neighbors", {
          id: input.id,
          kind: input.kind,
          ...(input.depth ? { depth: input.depth } : {}),
          ...(input.rels?.length ? { rels: input.rels } : {}),
        }),
      );
      if (raw.found !== true) {
        return null;
      }
      return WisdomNeighborhoodSchema.parse(raw);
    },

    path: async (input: {
      fromId: string;
      toId: string;
      fromKind: string;
      toKind: string;
      maxLen?: number;
      rels?: string[];
    }): Promise<WisdomPath> => {
      const raw = obj(
        await transport.post("wisdom.path", {
          fromId: input.fromId,
          toId: input.toId,
          kinds: [input.fromKind, input.toKind],
          ...(input.maxLen ? { maxLen: input.maxLen } : {}),
          ...(input.rels?.length ? { rels: input.rels } : {}),
        }),
      );
      return WisdomPathSchema.parse(raw);
    },

    provenance: async (input: { id: string; kind: string }): Promise<WisdomProvenance | null> => {
      const raw = obj(await transport.post("wisdom.provenance", input));
      if (raw.found !== true) {
        return null;
      }
      return WisdomProvenanceSchema.parse(raw);
    },

    overview: async (): Promise<WisdomOverview> => {
      const raw = obj(await transport.post("wisdom.overview", {}));
      return WisdomOverviewSchema.parse(raw);
    },

    /**
     * The project narrated top-down (components → documents → semantics → truths, with coverage,
     * tempo and MEASURED drives). Bare JSON; withheld is COUNTED server-side, never listed.
     */
    topdown: async (
      input: { scope?: "workspace" | "component"; key?: string; depth?: number } = {},
    ): Promise<WisdomTopDown> => {
      const raw = obj(
        await transport.post("wisdom.topdown", {
          ...(input.scope ? { scope: input.scope } : {}),
          ...(input.key ? { key: input.key } : {}),
          ...(input.depth ? { depth: input.depth } : {}),
        }),
      );
      return WisdomTopDownSchema.parse(raw);
    },

    /** `wisdom.coverage.code` answers `{data:{…}}` — unwrapped here, once. */
    coverageCode: async (): Promise<WisdomCoverage> => {
      const raw = obj(await transport.post("wisdom.coverage.code", {}));
      return WisdomCoverageSchema.parse(obj(raw.data));
    },

    /**
     * §E-3: an empty list is ambiguous between "no experts" and "the server swallowed an
     * exception". Callers must not render `[]` as "No experts found" — the wisdom scene
     * renders the softer "may not have analysed this topic yet" phrasing instead.
     */
    experts: async (query: string): Promise<Expert[]> => {
      const raw = obj(await transport.get(`wisdom/experts?q=${encodeURIComponent(query)}`));
      return arr(raw.experts).map((entry) => ExpertSchema.parse(entry));
    },
  };
}
